import { WelcomeVideoTelemetry, type WelcomeVideoCloseReason, type WelcomeVideoPlaybackSample, type WelcomeVideoRouteCategory } from '@/libs/analytics/WelcomeVideoTelemetry';
import { makeAutoObservable, runInAction } from 'mobx';
import type { IProductRepository } from '@/core/domain/repositories/IProductRepository';
import type { WelcomeVideoConfig } from '@/core/domain/entities/WelcomeVideo';
import { getWelcomeVideoSource } from '@/shared/utils/welcomeVideoSource';
import { getWelcomePlaylist, hasWelcomePlaylist } from '@/shared/utils/welcomePlaylist';
import type { QuickIntroHistoryStore } from './QuickIntroHistoryStore';
import { mergePlayedRanges } from '@/core/domain/entities/playedRanges';

const PREFERENCE_PREFIX = 'motito:welcome-video:v2:';
const SUPPRESSION_KEY = 'motito:welcome-video:suppressed:v1';
const VISIT_KEY = 'motito:welcome-video:visit:v1';
const MAX_IMPRESSIONS = 2;
const PAUSED_POLICY_RECHECK_MS = 60000;

type Preference = { impressions: number };
type StorageAccess = Pick<Storage, 'getItem' | 'setItem'>;
export type WelcomeVideoStorage = () => { local: StorageAccess; session: StorageAccess };

export class WelcomeVideoStore {
  config: WelcomeVideoConfig | null = null;
  private configLoading = false;
  private configUnavailable = false;
  private automaticConfigLoading = false;
  private automaticPolicyCheck: { config: WelcomeVideoConfig; checkedAt: number; failed: boolean } | null = null;
  isOpen = false;
  playbackRequested = false;
  mediaFailed = false;
  playbackNeedsInteraction = false;
  private automaticUnavailable = false;
  private telemetry: WelcomeVideoTelemetry | null = null;
  private pendingTelemetry: ConstructorParameters<typeof WelcomeVideoTelemetry> | null = null;
  selectedVideoId: string | null = null;
  routeCategory: WelcomeVideoRouteCategory = 'unknown';
  dialogToken = 0;
  private visibleToken = 0;
  private automaticEntry = false;
  private previouslyPlayed = false;
  private playbackStartedOnce = false;
  private playedRanges: [number, number][] = [];
  private duration = 0;
  private lastProgressSave = 0;

  constructor(private readonly storage: WelcomeVideoStorage, private readonly repository: Pick<IProductRepository, 'getWelcomeVideo'>,
    readonly history?: QuickIntroHistoryStore) {
    makeAutoObservable(this);
  }

  get source() {
    if (hasWelcomePlaylist(this.config)) {
      return this.selectedVideo ? getWelcomeVideoSource({ ...this.selectedVideo, enabled: true }) : null;
    }
    return getWelcomeVideoSource(this.config);
  }

  get videos() { return getWelcomePlaylist(this.config); }


  get selectedVideo() {
    return this.videos.find(video => video.id === this.selectedVideoId) ??
      this.videos.find(video => video.id === this.config?.main_video_id) ?? null;
  }

  selectVideo(id: string): void {
    if (!this.isOpen || !this.videos.some(video => video.id === id) || this.selectedVideo?.id === id) return;
    this.close('playlist_switch');
    this.selectedVideoId = id;
    this.open('playlist');
  }

  async loadConfig(): Promise<void> {
    if (this.configLoading) return;
    this.configLoading = true;
    this.configUnavailable = false;
    this.config = null;
    try {
      const config = await this.repository.getWelcomeVideo();
      runInAction(() => { this.config = config; });
    } catch {
      // A missing endpoint or unavailable optional content must not obstruct the catalogue.
      runInAction(() => { this.config = null; this.configUnavailable = true; });
    } finally {
      runInAction(() => { this.configLoading = false; });
    }
  }

  async recoverAvailability(accountId: string | null, canRecover: () => boolean): Promise<void> {
    if (!canRecover() || this.isOpen || this.automaticUnavailable) return;
    if (this.configUnavailable) await this.loadConfig();
    if (!canRecover() || this.isOpen || this.automaticUnavailable) return;
    const history = this.history;
    if (this.videos.length && history?.accountId === accountId && history.unavailable &&
      !history.loading && !history.state?.opted_out) {
      await history.load(accountId, this.videos);
    }
  }

  private get preferenceKey(): string | null {
    const source = this.source;
    if (!source) return null;
    const identity = source.provider === 'youtube' ? `youtube:${source.videoId}` : source.videoSrc;
    return `${PREFERENCE_PREFIX}${encodeURIComponent(identity)}`;
  }

  private readPreference(local: StorageAccess, key: string): Preference | null {
    const raw = local.getItem(key);
    if (raw === null) return { impressions: 0 };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const value = parsed as Partial<Preference>;
    return Number.isInteger(value.impressions) && value.impressions! >= 0 &&
      value.impressions! <= MAX_IMPRESSIONS
      ? value as Preference : null;
  }

  tryOpenAutomatic(): boolean {
    // New playlists use asynchronous, identity-aware admission below.
    if (hasWelcomePlaylist(this.config)) return false;
    const key = this.preferenceKey;
    if (!key || this.isOpen || this.automaticUnavailable) return false;
    try {
      const { local, session } = this.storage();
      const preference = this.readPreference(local, key);
      if (!preference || local.getItem(`${key}:completed`) === 'true' || local.getItem(SUPPRESSION_KEY) === 'true' || preference.impressions >= MAX_IMPRESSIONS || session.getItem(VISIT_KEY)) return false;
      // Persist only at admission, never when scheduling. If persistence fails, skip the interruption.
      session.setItem(VISIT_KEY, 'shown');
      local.setItem(key, JSON.stringify({ ...preference, impressions: preference.impressions + 1 }));
      this.open('automatic');
      return true;
    } catch {
      // Browser privacy/quota failures disable this optional invitation, not shopping.
      this.automaticUnavailable = true;
      return false;
    }
  }

  async tryOpenAutomaticAsync(canOpen: () => boolean): Promise<boolean> {
    if (!canOpen() || this.isOpen || this.automaticUnavailable) return false;
    if (!hasWelcomePlaylist(this.config)) return this.tryOpenAutomatic();
    const config = this.config!;
    if (this.automaticConfigLoading || !this.history?.canAttempt(config, this.videos)) return false;
    const checkedAt = performance.now();
    if (!Number.isFinite(checkedAt) || checkedAt < 0) return false;
    const previous = this.automaticPolicyCheck;
    if (previous?.config === config) {
      const elapsed = checkedAt - previous.checkedAt;
      if (elapsed < 0 || (elapsed < PAUSED_POLICY_RECHECK_MS &&
        (previous.failed || this.history.isCoolingDown(config)))) return false;
    }
    this.automaticPolicyCheck = { config, checkedAt, failed: true };
    this.automaticConfigLoading = true;
    try {
      const current = await this.repository.getWelcomeVideo();
      if (this.config !== config || this.isOpen || this.automaticUnavailable || !canOpen()) return false;
      if (JSON.stringify(current) !== JSON.stringify(config)) {
        // The normal config effect refreshes history before another safe opportunity.
        runInAction(() => {
          this.config = current;
          this.automaticPolicyCheck = { config: this.config, checkedAt, failed: false };
        });
        return false;
      }
      runInAction(() => { this.automaticPolicyCheck = { config, checkedAt, failed: false }; });
    } catch {
      // Unknown current policy must not interrupt shopping; manual access remains.
      return false;
    } finally {
      runInAction(() => { this.automaticConfigLoading = false; });
    }
    return this.history.admit(config, this.videos,
      () => this.config === config && !this.isOpen && !this.automaticUnavailable && canOpen(),
      (id, appearance) => { this.selectedVideoId = id; this.open('automatic', appearance); },
      () => this.closeUnengagedPrompt());
  }

  confirmVisible(): void {
    if (!this.isOpen || this.visibleToken === this.dialogToken) return;
    this.visibleToken = this.dialogToken;
    if (this.pendingTelemetry) this.telemetry = new WelcomeVideoTelemetry(...this.pendingTelemetry);
    this.pendingTelemetry = null;
    if (this.selectedVideo) void this.history?.visible(this.selectedVideo, this.automaticEntry);
  }

  openReplay(): void {
    if (this.isOpen || !this.source) return;
    if (hasWelcomePlaylist(this.config)) this.selectedVideoId = this.config?.main_video_id ?? null;
    try {
      this.storage().session.setItem(VISIT_KEY, 'shown');
    } catch {
      this.automaticUnavailable = true;
    }
    this.open('replay');
  }

  deferAutomaticForVisit(): void {
    this.close('competing_ui');
    try {
      this.storage().session.setItem(VISIT_KEY, 'deferred');
    } catch {
      // Keep the optional video out of this Wallet visit when storage is unavailable.
      this.automaticUnavailable = true;
    }
  }

  private open(entryPoint: 'automatic' | 'replay' | 'playlist', appearance = 0): void {
    if (this.isOpen || !this.source) return;
    const video = this.selectedVideo;
    this.pendingTelemetry = [this.source, entryPoint, video ? {
      videoId: video.id, mediaVersion: video.media_version,
      playlistRevision: this.config!.playlist_revision!,
      position: this.videos.findIndex(item => item.id === video.id) + 1,
      appearanceNumber: appearance, routeCategory: this.routeCategory,
    } : undefined];
    this.playbackRequested = false;
    this.mediaFailed = false;
    this.playbackNeedsInteraction = false;
    this.automaticEntry = entryPoint === 'automatic';
    this.previouslyPlayed = Boolean(video && this.history?.hasPlayed(video.id, video.media_version));
    this.playbackStartedOnce = false;
    this.playedRanges = [];
    this.duration = 0;
    this.lastProgressSave = Date.now();
    this.dialogToken += 1;
    this.isOpen = true;
  }

  closeUnengagedPrompt(reason: WelcomeVideoCloseReason = 'lease_expired'): void {
    if (this.isOpen && this.automaticEntry && !this.playbackRequested && !this.playbackStartedOnce) this.close(reason);
  }

  close(reason: WelcomeVideoCloseReason = 'system'): void {
    this.history?.releasePrompt();
    if (this.isOpen && this.visibleToken !== this.dialogToken) this.history?.abandon();
    if (this.isOpen && this.playbackStartedOnce && this.selectedVideo) void this.history?.progress(this.selectedVideo, this.playedRanges, this.duration);
    if (this.isOpen) this.telemetry?.close(reason);
    this.telemetry = null;
    this.pendingTelemetry = null;
    this.isOpen = false;
    this.playbackRequested = false;
  }

  requestPlayback(): void {
    this.history?.releasePrompt();
    if (!this.playbackRequested) this.telemetry?.request();
    this.playbackRequested = true;
    this.mediaFailed = false;
  }

  recordPlayedRanges(ranges: [number, number][], duration: number): void {
    if (!this.isOpen || !this.playbackStartedOnce || !Number.isFinite(duration) || duration <= 0) return;
    this.telemetry?.recordRanges(ranges, duration);
    this.playedRanges = mergePlayedRanges([...this.playedRanges, ...ranges], duration);
    this.duration = duration;
    if (this.playbackStartedOnce && this.selectedVideo && Date.now() - this.lastProgressSave >= 10000) {
      this.lastProgressSave = Date.now();
      void this.history?.progress(this.selectedVideo, this.playedRanges, this.duration);
    }
  }

  recordDialogVisibility(visible: boolean): void { this.telemetry?.recordVisibility(visible); }

  recordActivity(sample: WelcomeVideoPlaybackSample): void {
    this.telemetry?.recordActivity(sample);
  }

  playbackStarted(): void {
    this.history?.releasePrompt();
    this.telemetry?.start(this.previouslyPlayed);
    if (!this.playbackStartedOnce && this.selectedVideo) void this.history?.progress(this.selectedVideo, [], 0, { started: true });
    this.playbackStartedOnce = true;
    this.playbackNeedsInteraction = false;
  }

  requireNativePlay(): void {
    this.playbackNeedsInteraction = true;
  }

  failMedia(): void {
    this.telemetry?.fail();
    this.mediaFailed = true;
  }

  refreshSuppression(): void {
    try {
      if (this.storage().local.getItem(SUPPRESSION_KEY) === 'true') this.automaticUnavailable = true;
    } catch {
      this.automaticUnavailable = true;
    }
  }

  completeVideo(): void {
    this.telemetry?.end();
    if (hasWelcomePlaylist(this.config)) {
      if (this.playbackStartedOnce && this.selectedVideo) void this.history?.progress(this.selectedVideo, this.playedRanges, this.duration, { reached_end: true });
      return;
    }
    const key = this.preferenceKey;
    if (!key) return;
    try {
      const { local } = this.storage();
      // Completion must survive an overlapping tab writing an older impression count.
      local.setItem(`${key}:completed`, 'true');
    } catch {
      this.automaticUnavailable = true;
    }
  }

  suppressAutomatic(): void {
    this.telemetry?.suppress();
    this.automaticUnavailable = true;
    if (hasWelcomePlaylist(this.config)) void this.history?.suppress();
    try {
      // A separate monotonic preference cannot be overwritten by a parallel impression update.
      this.storage().local.setItem(SUPPRESSION_KEY, 'true');
    } catch {
      // Keep suppression in memory if this browser refuses persistence.
      this.automaticUnavailable = true;
    }
  }
}
